import { all, put, call, select, getContext, takeLatest } from 'redux-saga/effects';

import { Logger } from 'utils/Logger';
import { Analytics } from 'utils/Analytics';

import { AnalyticsEventCategory, AnalyticsEventAction } from 'enums/analytics-types';

import { CloseSearchReasonLabel } from 'enums/close-search-reason-type';
import { FundingMatchStatusLabel } from 'enums/funding-match-status';
import { LeadsTypes } from 'enums/leads-type';

import { requestLeads } from 'reducers/lead/actions';

import { updateMatchStatus, REQUEST_OFFER_UPLOAD, REQUEST_REMOVE_OFFER_DOCUMENT } from './types';

import {
  requestUpdateMatchStatusSuccess,
  requestUpdateMatchStatusFailure,
  requestOfferUploadSuccess,
  requestOfferUploadFailure,
  requestOfferUploadProgress,
} from './actions';

import { requestRemoveOfferSuccess } from '../actions';

import { makeSelectMasquerade, makeSelectUserType } from '../../auth/selectors';

import { requestFundingSearch, requestOffersHistory } from '../../fundingSearch/actions';

import { uploadFileWithProgressSaga } from '../../utils/FileUploader';

const logger = new Logger('Saga:Update Match Status');

export function* updateMatchAnalyticEvent(fundingMatchId, payload) {
  const isMasquerading = yield select(makeSelectMasquerade());
  const userType = yield select(makeSelectUserType());

  const extraParams = {
    userType,
    isMasquerading: !!isMasquerading,
    searchId: fundingMatchId,
    matchStatusSelected: FundingMatchStatusLabel[payload.targetStatus || payload.status],
    hasRequestAdditionalDocs: !!payload.additionalDocumentation,
    hasPrivateNote: !!payload.privateComment,
    offerExpiry: payload.offerExpireDateInWeeks,
    hasUpdateMessage: !!payload.publicComment,
    declineReason: CloseSearchReasonLabel[payload.reason],
  };

  Analytics.track(AnalyticsEventCategory.updateMatch, AnalyticsEventAction.statusUpdated, {
    extraParams,
  });

  Analytics.track(AnalyticsEventCategory.updateMatch, AnalyticsEventAction.updatedMatchStatus, {
    extraParams,
    joinCategory: false,
  });
}

export function* requestUpdateMatchStatusSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const { fundingMatchId, fundingSearchId, payload } = action;
  // eslint-disable-next-line no-unused-vars
  const { offerExpireDateInWeeks, ...data } = payload;

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.platform.funding_matches', { matchId: fundingMatchId }),
      data,
    );

    yield put(requestUpdateMatchStatusSuccess(response.data));

    // Refresh all lender tab columns
    yield put(requestLeads(LeadsTypes.active));
    yield put(requestLeads(LeadsTypes.requireAction));
    yield put(requestLeads(LeadsTypes.completed));
    yield put(requestFundingSearch(fundingSearchId));
    yield put(requestOffersHistory(fundingMatchId));
    yield call(updateMatchAnalyticEvent, action.fundingMatchId, action.payload);
  } catch (error) {
    logger.error('Failed to update match status with the following error', error);
    yield put(requestUpdateMatchStatusFailure(error));
  }
}

export function* requestUploadOfferSaga({ fundingMatchId, files }) {
  const routerService = yield getContext('routerService');

  try {
    const responses = yield all(
      files.map((file, index) => {
        const data = new FormData();
        data.append(`files[${index}]`, file);

        return call(
          uploadFileWithProgressSaga,
          routerService.generate('api.platform.funding_match_upload_offer_documents', {
            fundingMatchId,
          }),
          data,
          requestOfferUploadProgress,
        );
      }),
    );

    const responseData = responses.map((response) => response.data[0]);

    yield put(requestOfferUploadSuccess(responseData));
  } catch (error) {
    logger.error('Failed to upload offers with the following error', error);
    yield put(requestOfferUploadFailure(error));
  }
}

export function* requestRemoveOfferSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield call(
      authApiService.delete,
      routerService.generate('api.funding_match_remove_offer', {
        fundingMatchId: action.fundingMatchId,
        documentId: action.documentId,
      }),
    );

    yield put(requestRemoveOfferSuccess(action.documentId));
  } catch (error) {
    logger.error('Failed to update match status with the following error', error);
  }
}

export default function* updateMatchStatusSaga() {
  return yield all([
    takeLatest(updateMatchStatus.request, requestUpdateMatchStatusSaga),
    takeLatest(REQUEST_OFFER_UPLOAD, requestUploadOfferSaga),
    takeLatest(REQUEST_REMOVE_OFFER_DOCUMENT, requestRemoveOfferSaga),
  ]);
}
